import { AlertCircle } from "lucide-react";
import { Spinner } from "@/components/Spinner";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectValue, SelectItem, SelectTrigger, SelectSeparator } from "@/components/ui/select";
import { cn } from "@/lib/utils/common";
import { getErrorMessage } from "@/lib/utils/errors";

export type SelectOption = {
  value: string;
  label: string;
};

type SelectInputProps<T extends string> = {
  value?: T;
  label?: string;
  placeholder?: string;
  options: Array<SelectOption>;
  className?: string;
  defaultValue?: T;
  onChange?: (value: T) => void;
  "aria-label"?: string;
  id?: string;
  ref?: React.ForwardedRef<HTMLButtonElement>;
  loading?: boolean;
  disabled?: boolean;
  error?: unknown;
  showClear?: boolean;
};

export function SelectInput<T extends string>({
  label,
  placeholder = "Select",
  options,
  className,
  defaultValue,
  onChange,
  value,
  "aria-label": ariaLabel,
  id,
  ref,
  loading,
  disabled,
  error,
  showClear = true,
  ...props
}: SelectInputProps<T>) {
  // Currently, the recommended way to clear the select value
  // https://github.com/radix-ui/primitives/issues/2706#issuecomment-2347363637
  const handleSelectChange = (val: string) => {
    if (val === "_CLEAR_") {
      onChange?.("" as T);
    } else {
      onChange?.(val as T);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      {label ? (
        <Label className="mb-1" htmlFor={id}>
          {label}
        </Label>
      ) : null}
      <Select
        defaultValue={defaultValue}
        value={value}
        onValueChange={handleSelectChange}
        disabled={disabled}
        {...props}
      >
        <SelectTrigger
          ref={ref}
          className={cn(
            value ? "text-foreground" : "text-muted-foreground",
            !!error ? "border-red-500" : "",
            className,
          )}
          aria-label={ariaLabel}
          id={id}
        >
          {!!error ? (
            <div className="flex items-center justify-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <span>{getErrorMessage(error)}</span>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center gap-2">
              <Spinner />
              <span>Loading...</span>
            </div>
          ) : (
            <SelectValue placeholder={placeholder} />
          )}
        </SelectTrigger>
        <SelectContent>
          {showClear && (
            <>
              <SelectItem
                value="_CLEAR_"
                className={cn(
                  { "text-muted-foreground": !value },
                  "hover:cursor-pointer text-xs font-medium h-8 flex justify-center",
                )}
              >
                Clear
              </SelectItem>
              <SelectSeparator />
            </>
          )}
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
